import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DropdownWithActions, RadioOptionsList } from './Dropdown';
import { SingleSelectGroup } from '@xo-union/tk-component-switches';
import { PillButton } from './PillButton';

import styles from './styles.scss';
import Spinner from '@xo-union/tk-component-spinner';

const ESCAPE_KEY = 'Escape';

export type PillDropdownOptions = { text: string; value: string }[];

interface PillWithDropdownProps {
  text: string;
  options: PillDropdownOptions;
  setValue: (newValue: string) => void;
  defaultValue: string | undefined;
  selected: boolean;
  onPillClick?: () => void;
  onSaveClick?: () => void;
}

interface OutsideClickHookProps {
  onOutsideAction: () => void;
  elementRef: React.RefObject<HTMLDivElement>;
}

const useHandleOutsideClick = ({ onOutsideAction, elementRef }: OutsideClickHookProps) => {
  const handleOutsideClick = React.useCallback(
    (e: MouseEvent) => {
      if (!e.target || !elementRef.current) {
        return;
      }
      if (elementRef.current?.contains(e.target as Node)) {
        return;
      } else {
        onOutsideAction();
      }
    },
    [elementRef, onOutsideAction]
  );

  const handleEscapeClose = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ESCAPE_KEY) {
        onOutsideAction();
      }
    },
    [onOutsideAction]
  );

  React.useEffect(() => {
    document.addEventListener('keydown', handleEscapeClose);
    return () => document.removeEventListener('keydown', handleEscapeClose);
  }, [handleEscapeClose]);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [handleOutsideClick]);
};

export const PillWithRadioDropdown = ({
  text,
  options,
  setValue,
  defaultValue,
  selected: selectedActiveState,
  onPillClick,
  onSaveClick,
  ...rest
}: PillWithDropdownProps): JSX.Element | null => {
  const [showDrop, setShowDrop] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(defaultValue || '');
  const [saved, setSaved] = useState(defaultValue || '');

  useHandleOutsideClick({
    onOutsideAction: () => setShowDrop(false),
    elementRef: dropdownRef,
  });

  useEffect(() => {
    if (!showDrop && selected !== saved) {
      setSelected(saved);
    }
  }, [defaultValue, saved, selected, showDrop]);

  useEffect(() => {
    // reset to initial state
    if (!selectedActiveState) {
      setSelected(defaultValue || '');
      setSaved(defaultValue || '');
    }
  }, [defaultValue, selectedActiveState]);

  const handleChange = (value: string) => {
    setSelected(value);
  };

  const handlePillClick = useCallback(() => {
    // only on open clicks, not close clicks
    if (showDrop === false && !!onPillClick) onPillClick();
    setShowDrop(state => !state);
  }, [onPillClick, showDrop]);

  const handlePrimaryAction = useCallback(() => {
    setSaved(selected);
    setValue(selected);
    setShowDrop(false);
    if (onSaveClick) onSaveClick();
  }, [onSaveClick, selected, setValue]);

  const handleSecondaryAction = useCallback(() => {
    setSelected(defaultValue || '');
  }, [defaultValue]);

  return (
    <div ref={dropdownRef} className={styles.pillButtonWithDropdown}>
      <PillButton
        selected={showDrop || selectedActiveState}
        onClick={handlePillClick}
        aria-haspopup={true}
        aria-expanded={showDrop}
        {...rest}
      >
        {text}
      </PillButton>
      {!showDrop ? null : (
        <DropdownWithActions
          primaryButtonText="Save"
          secondaryButtonText="Reset"
          primaryButtonAction={handlePrimaryAction}
          secondaryButtonAction={handleSecondaryAction}
          primaryButtonDisabled={selected === saved}
        >
          {options.length > 1 ? (
            <SingleSelectGroup value={selected} onValueChange={handleChange}>
              <RadioOptionsList options={options} />
            </SingleSelectGroup>
          ) : (
            <Spinner />
          )}
        </DropdownWithActions>
      )}
    </div>
  );
};
